import type { Editor } from "@tiptap/core";

export const SELECTION_ENHANCE_MIN_WORDS = 20;
export const SELECTION_ENHANCE_MAX_WORDS = 600;

export type CapturedSelectionEnhanceRange = {
  from: number;
  paragraphContext: string;
  selectedText: string;
  to: number;
  wordCount: number;
};

export function countSelectionWords(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function hasNonEmptySelection(editor: Editor | null) {
  if (!editor) {
    return false;
  }

  const { from, to } = editor.state.selection;
  return from !== to && editor.state.doc.textBetween(from, to, " ").trim().length > 0;
}

export function getSelectedText(editor: Editor) {
  const { from, to } = editor.state.selection;
  return editor.state.doc.textBetween(from, to, " ").trim();
}

export function getParagraphContext(editor: Editor, position: number) {
  const resolved = editor.state.doc.resolve(position);
  for (let depth = resolved.depth; depth >= 0; depth -= 1) {
    const node = resolved.node(depth);
    if (node.type.name === "paragraph" || node.type.name === "heading") {
      const start = resolved.start(depth);
      const end = resolved.end(depth);
      return editor.state.doc.textBetween(start, end, " ").trim();
    }
  }

  return getSelectedText(editor);
}

export function captureSelectionEnhanceRange(editor: Editor): CapturedSelectionEnhanceRange | null {
  if (!hasNonEmptySelection(editor)) {
    return null;
  }

  const { from, to } = editor.state.selection;
  const selectedText = getSelectedText(editor);

  return {
    from,
    paragraphContext: getParagraphContext(editor, from),
    selectedText,
    to,
    wordCount: countSelectionWords(selectedText),
  };
}
